import { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FormContainer from "../components/FormContainer";
import Loader from "../components/Loader";
import { useLoginMutation } from "../slices/usersApiSlice";
import { setCredentials } from "../slices/authSlice";
import { toast } from "react-toastify";
import Meta from "../components/Meta";

/** 自サイト内への安全なパスだけを返す */
const toSafeInternalPath = (raw, fallback = "/") => {
  if (!raw) return fallback;
  try {
    // decode して検証（%2F などの混入に備える）
    const decoded = decodeURIComponent(raw);

    // 先頭が "/" で、"//" から始まらず、"http" 等のスキームを含まない
    const isInternal =
      decoded.startsWith("/") &&
      !decoded.startsWith("//") &&
      !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(decoded);

    return isInternal ? decoded : fallback;
  } catch {
    return fallback;
  }
};

/** ロール別のデフォルト遷移先 */
const roleFallback = (role) => {
  if (role === "shop") return "/shop/orderlist";
  if (role === "admin") return "/admin/dashboard";
  return "/";
};

const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const { search } = useLocation();
  const sp = useMemo(() => new URLSearchParams(search), [search]);

  // redirect クエリを安全化（無ければ "/"）
  const redirectRaw = sp.get("redirect");
  const safeRedirect = toSafeInternalPath(
    redirectRaw,
    roleFallback(userInfo?.role)
  );

  // 既にログイン済みなら即リダイレクト（履歴を汚さない）
  useEffect(() => {
    if (userInfo) {
      const next = safeRedirect || roleFallback(userInfo.role);
      navigate(next, { replace: true });
    }
  }, [userInfo, safeRedirect, navigate]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("メールアドレスとパスワードを入力してください");
      return;
    }
    try {
      const res = await login({ email: email.trim(), password }).unwrap();
      dispatch(setCredentials({ ...res }));
      const next = safeRedirect || roleFallback(res?.user?.role || res?.role);
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "ログインに失敗しました");
    }
  };

  return (
    <>
      <Meta
        title="ログイン | ふるぷら"
        description="ふるぷらのログインページです。"
        keywords={["ログイン", "ふるぷら", "古着", "下北", "古着屋", "高円寺"]}
      />
      <FormContainer>
        <h1>Sign In</h1>

        <Form onSubmit={submitHandler}>
          <Form.Group controlId="email" className="my-3">
            <Form.Label>メールアドレス</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </Form.Group>

          <Form.Group controlId="password" className="my-3">
            <Form.Label>パスワード</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            className="mt-2"
            disabled={isLoading}
          >
            ログイン
          </Button>

          {isLoading && <Loader />}
        </Form>

        <Row className="py-3">
          <Col>
            初めての方は{" "}
            <Link
              to={
                redirectRaw
                  ? `/register?redirect=${encodeURIComponent(
                      toSafeInternalPath(redirectRaw, "/")
                    )}`
                  : "/register"
              }
            >
              こちら
            </Link>
            {" "}からアカウントを作成してください
          </Col>
        </Row>
      </FormContainer>
    </>
  );
};

export default LoginScreen;
